import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const handleCategoryClick = () => {
    setIsDropdownOpen(false);
    setIsMobileOpen(false);
  };

  const logoTextClass = isTransparent
    ? 'text-white'
    : 'text-foreground-950 hover:text-primary-500';

  const navLinkClass = isTransparent
    ? 'text-white/90 hover:text-white'
    : 'text-foreground-700 hover:text-primary-500';

  const dropdownBtnClass = isTransparent
    ? 'bg-white/15 text-white border-white/20 hover:bg-white/25'
    : 'bg-background-100 hover:bg-background-200 text-foreground-700 border-background-200/70';

  const ctaBtnClass = isTransparent
    ? 'bg-white text-foreground-950 hover:bg-background-100'
    : 'bg-primary-500 text-background-50 hover:bg-primary-600';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <a
            href="/"
            className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap cursor-pointer transition-colors duration-200 ${logoTextClass}`}
          >
            litbuyspreadsheets.mom
          </a>

          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            <a
              href="/"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 ${navLinkClass}`}
            >
              Home
            </a>

            <a
              href="/article/what-is-litbuy-spreadsheet"
              className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 ${navLinkClass}`}
            >
              Shopping Guide
            </a>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 border ${dropdownBtnClass}`}
              >
                <span>Shop Categories</span>
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={handleCategoryClick}
                      className="block w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 whitespace-nowrap cursor-pointer transition-colors duration-150"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 ${ctaBtnClass}`}
            >
              litbuy spreadsheet
            </a>
          </div>

          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors duration-200 ${
              isTransparent ? 'text-white' : 'text-foreground-950'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg animate-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-3 space-y-1">
            <a
              href="/"
              onClick={() => setIsMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors duration-150"
            >
              <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium">Home</span>
            </a>

            <a
              href="/article/what-is-litbuy-spreadsheet"
              onClick={() => setIsMobileOpen(false)}
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors duration-150"
            >
              <i className="ri-book-open-line text-lg w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium">Shopping Guide</span>
            </a>

            <div className="border-t border-background-200/70 my-2"></div>

            <p className="px-3 py-1 text-xs text-foreground-400 uppercase tracking-wider font-semibold">
              Shop Categories
            </p>

            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={handleCategoryClick}
                  className="px-3 py-2.5 rounded-lg text-left text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-600 cursor-pointer transition-colors duration-150 whitespace-nowrap truncate"
                >
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="border-t border-background-200/70 my-2"></div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsMobileOpen(false)}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-all duration-200"
            >
              litbuy spreadsheet
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}